import { assert, BadRequestError, InternalError } from '@fragment/error-utils';

// This is BigInt(2) ** BigInt(120) - BigInt(1)
export const int96MaxValue = 1329227995784915872903807060280344575n;
export const int96MinValue = -int96MaxValue;

export const validateInt96ValueRange = (value: bigint) => {
  if (value < int96MinValue)
    throw new TypeError(
      `Values less than ${int96MinValue} are currently unsupported`
    );
  if (value > int96MaxValue)
    throw new TypeError(
      `Values greater than ${int96MaxValue} are currently unsupported`
    );
};

type TokenType = 'number' | '+' | '-';

type Token = {
  type: TokenType;
  start: number;
  end: number;
  repr: string;
};

const tokenize = (expression: string): Token[] => {
  const re = /\d+|[-+]|\s+/dy;
  let pos = 0;
  const tokens: Token[] = [];
  let match = re.exec(expression);
  while (match !== null) {
    const start = pos;
    const end = re.lastIndex;
    const repr = match[0];
    if (!/\s/.test(repr)) {
      let type: TokenType;
      if (repr === '+' || repr === '-') {
        type = repr;
      } else {
        type = 'number';
      }
      tokens.push({ type, start, end, repr });
    }
    pos = end;
    match = re.exec(expression);
  }
  assert(pos === expression.length, BadRequestError, () => ({
    message: `Unexpected character ${expression.charAt(
      pos
    )} at position ${pos}`,
  }));
  assert(tokens.length > 0, BadRequestError, () => ({
    message: `Empty expression cannot be evaluated`,
  }));
  return tokens;
};

export const evaluateExpression = (expression: string): bigint => {
  const tokens = tokenize(expression);
  let i = 0;

  const assertNextToken = () => {
    assert(tokens[i + 1] !== undefined, BadRequestError, () => ({
      message: `Unexpected end of expression at position ${
        tokens[i].start + tokens[i].repr.length
      }`,
    }));
  };

  const term = (): bigint => {
    const t = tokens[i];
    assert(t !== undefined, BadRequestError, () => ({
      message: `Unexpected end of expression at position ${expression.length}`,
    }));
    // We could add ( and ) and check here for parens if we want to support.
    if (t.type === '+') {
      assertNextToken();
      i += 1;
      return term();
    }
    if (t.type === '-') {
      assertNextToken();
      i += 1;
      return -term();
    }
    const value = BigInt(tokens[i].repr);
    i += 1;
    return value;
  };

  const assertTokenIsOp = () => {
    const t = tokens[i];
    assert(t.type === '-' || t.type === '+', BadRequestError, () => ({
      message: `expected operator + or - at position ${t.start}, found ${t.repr}`,
    }));
  };

  const expr = (): bigint => {
    let value = term();
    while (i < tokens.length) {
      assertTokenIsOp();
      value += term();
    }
    return value;
  };

  const value = expr();

  try {
    validateInt96ValueRange(value);
    return value;
  } catch (err) {
    // We should get a TypeError from validateInt96ValueRange if the number is out of range
    assert(err instanceof TypeError, InternalError, () => ({
      message: 'Unexpected error type',
      info: { err },
    }));
    // The error message from `validateInt96Range` is safe to return to users, as it will bubble up
    // via graphql errors. So we can include it here.
    throw new BadRequestError((err as TypeError).message);
  }
};

type TokenWVarsType = 'number' | '+' | '-' | 'var';

type TokenWVars = {
  type: TokenWVarsType;
  start: number;
  end: number;
  repr: string;
};

const tokenizeWithVars = (expression: string): TokenWVars[] => {
  const re = /\d+|[-+]|\s+|{{[^{}]*}}/dy;
  let pos = 0;
  const tokens: TokenWVars[] = [];
  let match = re.exec(expression);
  while (match !== null) {
    const start = pos;
    const end = re.lastIndex;
    const repr = match[0];
    if (!/^\s+$/.test(repr)) {
      let type: TokenWVarsType;
      if (repr === '+' || repr === '-') {
        type = repr;
      } else if (repr.startsWith('{{') && repr.endsWith('}}')) {
        type = 'var';
      } else {
        type = 'number';
      }
      tokens.push({ type, start, end, repr });
    }
    pos = end;
    match = re.exec(expression);
  }
  assert(pos === expression.length, BadRequestError, () => ({
    message: `Unexpected character ${expression.charAt(
      pos
    )} at position ${pos}`,
  }));
  assert(tokens.length > 0, BadRequestError, () => ({
    message: `Empty expression cannot be evaluated`,
  }));
  return tokens;
};

export type VarRecord = Map<string, number>;

export const evaluateExpressionWithVars = (
  expression: string
): [bigint, VarRecord] => {
  const tokens = tokenizeWithVars(expression);
  let i = 0;

  const assertNextToken = () => {
    assert(tokens[i + 1] !== undefined, BadRequestError, () => ({
      message: `Unexpected end of expression at position ${
        tokens[i].start + tokens[i].repr.length
      }`,
    }));
  };

  const term = (): bigint | [number, string] => {
    const t = tokens[i];
    assert(t !== undefined, BadRequestError, () => ({
      message: `Unexpected end of expression at position ${expression.length}`,
    }));
    // We could add ( and ) and check here for parens if we want to support.
    if (t.type === '+') {
      assertNextToken();
      i += 1;
      return term();
    }
    if (t.type === '-') {
      assertNextToken();
      i += 1;
      const nextTerm = term();
      return typeof nextTerm === 'bigint'
        ? -nextTerm
        : [-nextTerm[0], nextTerm[1]];
    }
    if (t.type === 'var') {
      i += 1;
      return [1, t.repr.substring(2, t.repr.length - 2).trim()];
    }
    const value = BigInt(tokens[i].repr);
    i += 1;
    return value;
  };

  const assertTokenIsOp = () => {
    const t = tokens[i];
    assert(t.type === '-' || t.type === '+', BadRequestError, () => ({
      message: `expected operator + or - at position ${t.start}, found ${t.repr}`,
    }));
  };

  const expr = (): [bigint, VarRecord] => {
    const firstTerm = term();
    let value: [bigint, VarRecord] =
      typeof firstTerm === 'bigint'
        ? [firstTerm, new Map()]
        : [0n, new Map([[firstTerm[1], firstTerm[0]]])];
    while (i < tokens.length) {
      assertTokenIsOp();
      const nextTerm = term();
      value =
        typeof nextTerm === 'bigint'
          ? [value[0] + nextTerm, value[1]]
          : [
              value[0],
              new Map(value[1]).set(
                nextTerm[1],
                (value[1].get(nextTerm[1]) ?? 0) + nextTerm[0]
              ),
            ];
    }
    return [value[0], new Map(Array.from(value[1]).filter(([, n]) => n !== 0))];
  };

  const value = expr();

  try {
    validateInt96ValueRange(value[0]);
    return value;
  } catch (err) {
    // We should get a TypeError from validateInt96ValueRange if the number is out of range
    assert(err instanceof TypeError, InternalError, () => ({
      message: 'Unexpected error type',
      info: { err },
    }));
    // The error message from `validateInt96Range` is safe to return to users, as it will bubble up
    // via graphql errors. So we can include it here.
    throw new BadRequestError((err as TypeError).message);
  }
};

export const add = (
  a: [bigint, VarRecord],
  b: [bigint, VarRecord]
): [bigint, VarRecord] => {
  const keys = new Set([...a[1].keys(), ...b[1].keys()]);
  const result = new Map(
    Array.from(keys)
      .map((key) => [key, (a[1].get(key) ?? 0) + (b[1].get(key) ?? 0)] as const)
      .filter(([, n]) => n !== 0)
  );
  return [a[0] + b[0], result];
};

export const subtract = (
  a: [bigint, VarRecord],
  b: [bigint, VarRecord]
): [bigint, VarRecord] => {
  const keys = new Set([...a[1].keys(), ...b[1].keys()]);
  const result = new Map(
    Array.from(keys)
      .map((key) => [key, (a[1].get(key) ?? 0) - (b[1].get(key) ?? 0)] as const)
      .filter(([, n]) => n !== 0)
  );
  return [a[0] - b[0], result];
};

export const areExpressionsEqual = (
  a: [bigint, VarRecord],
  b: [bigint, VarRecord]
): boolean => {
  const variables = new Set([...a[1].keys(), ...b[1].keys()]);
  return (
    a[0] === b[0] &&
    [...variables].every((key) => (a[1].get(key) ?? 0) === (b[1].get(key) ?? 0))
  );
};

function abs(bigint: bigint): bigint {
  return bigint < 0n ? -bigint : bigint;
}

export const zero: [bigint, VarRecord] = [0n, new Map()] as const;

type ToStringOptions = {
  varMap?: (v: string) => string;
  noCoefficients?: boolean;
};

export const toString = (
  value: [bigint, VarRecord],
  options: ToStringOptions = {}
): string => {
  const varMap = options.varMap ?? ((v) => v);
  const noCoefficients = options.noCoefficients ?? false;
  const terms = Array.from(value[1].entries())
    .filter(([, n]) => n !== 0)
    .sort((a, b) => b[1] - a[1])
    .map(([key, n]) =>
      noCoefficients
        ? ` ${n >= 0 ? '+' : '-'} ${varMap(key)}`
            .repeat(Math.abs(n))
            .substring(1)
        : `${n >= 0 ? '+' : '-'} ${
            Math.abs(n) === 1 ? '' : `${Math.abs(n)} * `
          }${varMap(key)}`
    )
    .join(' ');

  if (terms === '') {
    return value[0].toString();
  }
  let tailValue = '';
  if (value[0] !== 0n) {
    tailValue =
      value[0] > 0n
        ? ` + ${value[0].toString()}`
        : ` - ${abs(value[0]).toString()}`;
  }

  return `${terms.startsWith('+ ') ? '' : '-'}${terms.substring(
    2
  )}${tailValue}`;
};
